// Tiny formatting helpers - kept small to avoid a third-party dep.
//
// All numeric formatters use `Intl.NumberFormat` with the user's default
// locale (no explicit locale arg) so a user in en-US sees `10,000` while
// a user in de-DE sees `10.000` and a user in fr-FR sees `10 000`. In a
// browser context this picks up `navigator.language` automatically.

export function shortAddress(addr: string, head = 4, tail = 4): string {
  if (!addr) return "";
  if (addr.length <= head + tail + 1) return addr;
  return `${addr.slice(0, head)}…${addr.slice(-tail)}`;
}

/**
 * Format a nanoTON value as a human-readable TON amount with locale-aware
 * thousands separators.
 *
 * Examples (en-US locale):
 *   fmtTon(100_000_000n)          → "0.1"
 *   fmtTon(10_000_000_000_000n)   → "10,000"
 *   fmtTon(1_500_000_000n, 4)     → "1.5"
 *
 * @param nano  nanoTON value (1 TON = 1e9 nanoTON). bigint preferred to
 *              preserve precision on large denominations.
 * @param dp    max fractional digits to show. Trailing zeros are trimmed.
 */
export function fmtTon(nano: bigint | string | number, dp = 4): string {
  const n = typeof nano === "bigint" ? nano : BigInt(nano);
  const whole = n / 1_000_000_000n;
  const frac = n % 1_000_000_000n;
  // Locale-aware integer formatting via Intl.NumberFormat. Whole is a
  // bigint; the constructor accepts bigint directly.
  const wholeStr = new Intl.NumberFormat().format(whole);
  if (frac === 0n) return wholeStr;
  const fracStr = frac.toString().padStart(9, "0").slice(0, dp).replace(/0+$/, "");
  return fracStr ? `${wholeStr}.${fracStr}` : wholeStr;
}

/**
 * Format a human-readable amount string (e.g. "10000") as a locale-aware
 * display amount (e.g. "10,000" in en-US, "10.000" in de-DE).
 *
 * Accepts either a decimal string or a number. Returns a string suitable
 * for direct rendering. Use this for pool denomination labels in the UI.
 *
 * Examples (en-US locale):
 *   fmtAmount("100000") → "100,000"
 *   fmtAmount("0.1")    → "0.1"
 *   fmtAmount("1.5")    → "1.5"
 */
export function fmtAmount(amount: string | number): string {
  const s = typeof amount === "number" ? amount.toString() : amount;
  // Split on decimal separator if present; format the integer part with
  // grouping, keep the fractional part verbatim.
  const dotIdx = s.indexOf(".");
  if (dotIdx === -1) {
    // Pure integer — format via bigint to avoid Number-precision loss on
    // values like "10000000000000".
    try {
      return new Intl.NumberFormat().format(BigInt(s));
    } catch {
      return s; // fall back to raw string if the input wasn't a clean integer
    }
  }
  const intPart = s.slice(0, dotIdx);
  const fracPart = s.slice(dotIdx + 1);
  let intFormatted: string;
  try {
    intFormatted = new Intl.NumberFormat().format(BigInt(intPart || "0"));
  } catch {
    intFormatted = intPart;
  }
  // Use the locale's decimal separator. Intl.NumberFormat.formatToParts
  // tells us what that character is (e.g. "." in en-US, "," in de-DE).
  const decimalSep =
    new Intl.NumberFormat().formatToParts(1.1).find((p) => p.type === "decimal")?.value ?? ".";
  return `${intFormatted}${decimalSep}${fracPart}`;
}
